import THDStorageUtils, { LOCAL_STORAGE, SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import SpeechRecognition from 'react-speech-recognition';
import {
  SEARCH_HISTORY_STORAGE_KEY, ERROR, CHANGE_TYPE, B2C_LOADOUT_GROUP_NAME
} from './AddToConstants';

const {
  EMPTY_GROUP_NAME_MESSAGE,
  INVALID_GROUP_LENGTH_MESSAGE,
  INVALID_PROJECT_TEXT_MESSAGE
} = ERROR;

const runSpeechCommand = (command) => {
  try {
    const result = command?.();

    if (result && typeof result.then === 'function') {
      result.catch(() => {
        // Ignore stop/abort rejections; we retry below.
      });
    }
  } catch {
    // Ignore sync stop/abort errors; we retry below.
  }
};

const stopNativeRecognition = () => {
  if (typeof SpeechRecognition.getRecognition !== 'function') {
    return;
  }

  const recognition = SpeechRecognition.getRecognition();

  if (!recognition) {
    return;
  }

  runSpeechCommand(() => recognition.stop());
  runSpeechCommand(() => recognition.abort());
};

export const stopListeningNow = () => {
  runSpeechCommand(() => SpeechRecognition.stopListening());
  runSpeechCommand(() => SpeechRecognition.abortListening());
  stopNativeRecognition();
};
export const fetchSearch = () => {
  try {
    const history = THDStorageUtils.get(LOCAL_STORAGE, { key: SEARCH_HISTORY_STORAGE_KEY });
    if (!history || history === 'undefined') {
      return [];
    }
    const parsedHistory = JSON.parse(history);
    return Array.isArray(parsedHistory) ? parsedHistory.slice(0, 10) : [];
  } catch (err) {
    // Handle both storage access errors and JSON parsing errors
    // eslint-disable-next-line no-console
    console.error('Error retrieving search history:', err);
    return [];
  }
};

export const searchHystoryFormat = (data) => {
  return data?.map(({ keyword }) => {
    const cleanKeyword = decodeURIComponent(keyword) || keyword;
    return ({ link: `/s/${keyword}`, title: cleanKeyword });
  });
};

export const generateLoadedProducts = (existingProducts, newProducts) => {
  const existingItemIds = existingProducts.map((product) => product.itemId);
  const productsToLoad = newProducts?.filter((product) => !existingItemIds.includes(product.id)) || [];
  return [...existingProducts, ...productsToLoad];
};

export const getNvalue = (path) => {
  const pathArr = path.split('/');
  const fullNValue = pathArr.find((part) => part.startsWith('N-') || part.startsWith('Ntt-'));
  if (fullNValue) {
    const nValue = fullNValue.split('?')?.[0];
    if (nValue.startsWith('N-')) {
      return nValue.slice(2);
    }
    return nValue.slice(4);
  }
  return null;
};

export const getNewKeyword = (path) => {
  const pathArr = path.split('/');
  const indexOfs = pathArr.findIndex((part) => part === 's');
  const keywordPart = pathArr?.[indexOfs + 1];
  if (keywordPart) {
    const keywordArr = keywordPart.split('?');
    return keywordArr?.[0] || null;
  }
  return null;
};

export const triggerAnalytics = (event, payload = {}) => {
  LIFE_CYCLE_EVENT_BUS.trigger(event, payload);
};

// Cleans and encodes intent text for safe transmission to backend
// Replaces tilde with hyphen and URI encodes the result
export const cleanIntent = (intent) => {
  const updatedIntent = (intent || '').trim().replace(/~/g, '-');
  return encodeURIComponent(updatedIntent);
};

export const validateGroupName = (value) => {
  const validCharacters = /^(?=.{0,50}$)[ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]+$/;
  const blank = !(value || '').replace(/\s/g, '');
  let isValid = validCharacters.test(value);
  const charLimit = value?.length > 50;
  let errorMessage = '';
  if (!isValid) {
    if (blank) {
      errorMessage = EMPTY_GROUP_NAME_MESSAGE;
    } else if (charLimit) {
      errorMessage = INVALID_GROUP_LENGTH_MESSAGE;
    } else {
      errorMessage = INVALID_PROJECT_TEXT_MESSAGE;
    }
  }
  return { isError: !isValid, errorMessage };
};

export const getProductsByType = (products) => {
  if (!products?.length) {
    return {
      qcProducts: [],
      nonSharedQcProducts: [],
      majorAppliances: [],
      configurableBlinds: [],
      liveGoods: [],
      discontinuedOutOfStockUnavailable: []
    };
  }

  return {
    qcProducts: products.filter((product) => product.flag === 'QC_PRODUCT'),
    nonSharedQcProducts: products.filter((product) => product.flag === 'NON_SHARED_QC_PRODUCT'),
    majorAppliances: products.filter((product) => product.flag === 'MAJOR_APPLIANCE'),
    configurableBlinds: products.filter((product) => product.flag === 'CONFIGURABLE_BLINDS'),
    liveGoods: products.filter((product) => product.flag === 'LIVE_GOODS'),
    discontinuedOutOfStockUnavailable: products.filter((product) => product.flag === 'DISCONTINUED_OOS_UNAVAILABLE')
  };
};

export const formatProductsforCopy = (products) => {
  return products.map((product) => (
  `Product: ${product?.identifiers?.productLabel || product?.product?.description || 'N/A'}\n\
  SKU: ${product?.identifiers?.storeSkuNumber || product?.product?.sku || 'N/A'}\n\
  Brand: ${product?.identifiers?.brandName || 'N/A'}\n\
  Quantity: ${product?.quantity || product?.product?.quantity || 'N/A'}\n\
  itemId: ${product?.itemId || 'N/A'}\n\
  Type: ${product?.flag}`
  )).join('\n\n');
};

const filterNonQuoteableCatalogProducts = (productsData) => {
  if (!productsData) {
    return [];
  }
  const filteredProducts = productsData.filter((product) => {
    return (
      product?.flag === 'MAJOR_APPLIANCE'
      || product?.flag === 'CONFIGURABLE_BLINDS'
      || product?.flag === 'LIVE_GOODS'
      || product?.flag === 'DISCONTINUED_OOS_UNAVAILABLE'
    );
  });
  return filteredProducts;
};

export const updateNonQuoteableProducts = (catalogItem, qCItems, nonSharedQCItems) => {
  const newProducts = [];
  if (qCItems?.length) {
    newProducts.push(...qCItems);
  }
  if (nonSharedQCItems?.length) {
    newProducts.push(...nonSharedQCItems);
  }
  if (catalogItem?.length) {
    const filteredProducts = filterNonQuoteableCatalogProducts(catalogItem);
    newProducts.push(...filteredProducts);
  }
  return newProducts;
};

export const handleOpenATQ = (buttonRef) => {
  if (buttonRef.current) {
    buttonRef.current.click();
  }
};

export const getValidSku = (skuData, identifiers = {}) => {
  if (typeof skuData === 'string') {
    return skuData;
  }

  const {
    storeSkuNumber: storeSkuFromSku,
    omsThdSku: omsSkuFromSku,
    specialOrderSku: specialOrderSkuFromSku
  } = skuData || {};
  const {
    storeSkuNumber,
    omsThdSku,
    specialOrderSku
  } = identifiers || {};

  return storeSkuFromSku || omsSkuFromSku || specialOrderSkuFromSku
    || storeSkuNumber || omsThdSku || specialOrderSku;
};

export const isItemIntent = (itemData) => {
  return itemData?.itemType === 'intent' && !!itemData?.purchaseIntent;
};

export const triggerLoadingDrawerAnalytics = (message, startTime) => {
  const elapsedSeconds = Math.floor(Date.now() / 1000) - startTime;
  window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.closeLoadingScreen',
    { settings: `add to quote loading screen overlay: ${message}: ${elapsedSeconds}` }
  );
};

export const onLocalizationUpdate = () => {
  THDStorageUtils.set(
    SESSION_STORAGE,
    { key: 'localizedToProjectFromAddTo', value: 'true' }
  );
};

export const generateId = () => {
  const fallbackId = () => {
    return '_' + Math.random().toString(36).slice(2, 11);
  };
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return fallbackId();

  } catch (error) {
    return fallbackId();
  }
};

export const cleanQuantity = (quantity) => {
  return Math.min(
    Math.max(Number(quantity) || 1, 1),
    9999
  );
};

export const shouldDeselectGroupByDefault = (groupName) => {
  if (!groupName || typeof groupName !== 'string') return false;

  const lowerGroupName = groupName.toLowerCase().trim();

  const isDemolitionGroup = lowerGroupName.includes('demolition') && lowerGroupName.includes('prep');

  const isLoadoutGroup = lowerGroupName.includes('loadout') && lowerGroupName.includes('list');

  const isSiteCleanupGroup = lowerGroupName.includes('site cleanup')
    || (lowerGroupName.includes('site') && lowerGroupName.includes('wrap'));

  return isDemolitionGroup || isLoadoutGroup || isSiteCleanupGroup;
};

const isLoadoutGroup = (groupName) => {
  if (!groupName || typeof groupName !== 'string') return false;
  const lowerGroupName = groupName.toLowerCase().trim();
  return lowerGroupName.includes('loadout') && lowerGroupName.includes('list');
};

export const formatMaterials = (materialsGroups, applyDefaultDeselection = true, customerType = null) => {
  const isB2C = customerType?.toUpperCase() !== 'B2B';

  return (materialsGroups || []).map((materialGroup, groupIndex) => {
    const shouldBeDeselected = applyDefaultDeselection
      && !isB2C
      && shouldDeselectGroupByDefault(materialGroup.groupName);

    const groupName = (isB2C && isLoadoutGroup(materialGroup.groupName))
      ? B2C_LOADOUT_GROUP_NAME
      : materialGroup.groupName;

    return {
      ...materialGroup,
      groupName,
      id: generateId(),
      itemOrder: groupIndex,
      materials: materialGroup.materials.map((material, itemIndex) => ({
        ...material,
        quantity: cleanQuantity(material.quantity),
        id: generateId(),
        itemOrder: itemIndex,
        isSelected: !shouldBeDeselected
      }))
    };
  });
};

export const getSelectedMaterials = (groupedIntents) => {
  return (groupedIntents || []).map((group) => ({
    ...group,
    materials: (group.materials || []).filter((item) => item.isSelected)
  })).filter((group) => (group.materials || []).length > 0);
};

/**
 * Determines the drawer header title based on screen state and conditions
 * @param {Object} params - Parameters object
 * @param {string} params.screen - Current screen identifier
 * @param {boolean} params.showSuccessMessage - Whether success message is shown
 * @param {boolean} params.isIntent - Whether current context is an intent
 * @param {Object} params.HEADER_TEXT - Header text constants
 * @param {string} params.DEFAULT - Default screen constant
 * @returns {string} - The header title to display
 */
export const getDrawerTitle = ({
  screen, showSuccessMessage, isIntent, HEADER_TEXT, DEFAULT, isDisplayOnly
}) => {
  if (isDisplayOnly && HEADER_TEXT[screen]) {
    return HEADER_TEXT[screen];
  }
  if ((showSuccessMessage && screen === DEFAULT) || isIntent) {
    return HEADER_TEXT.ADD_TO_PROJECT;
  }
  // PROJECT_ZERO_STATE uses its own header text from HEADER_TEXT
  return HEADER_TEXT[screen] || HEADER_TEXT.ADD_TO_PROJECT;
};

/**
 * Determines if the back button should be shown based on current screen and conditions
 * @param {Object} params - Parameters object
 * @param {string} params.screen - Current screen identifier
 * @param {string} params.addToType - Type of add-to operation
 * @param {string} params.PROJECT_SELECT_PROJECT - Project select screen constant
 * @param {string} params.PROJECT_CREATE_PROJECT - Project create screen constant
 * @param {string} params.DEFAULT - Default screen constant
 * @param {Array} params.loadedProjects - Array of loaded projects
 * @returns {boolean} - Whether back button should be shown
 */
export const shouldShowBack = ({
  screen,
  addToType,
  PROJECT_SELECT_PROJECT,
  PROJECT_CREATE_PROJECT,
  PROJECT_ZERO_STATE,
  DEFAULT,
  loadedProjects,
  isDisplayOnly,
  MINI_PIP_BASE
}) => {
  const screensWithoutBackButton = [
    DEFAULT,
    PROJECT_ZERO_STATE,
    ...(addToType !== 'Cart' ? [PROJECT_SELECT_PROJECT] : []),
    ...(PROJECT_CREATE_PROJECT && !loadedProjects?.length ? [PROJECT_CREATE_PROJECT] : []),
    ...(isDisplayOnly && MINI_PIP_BASE ? [MINI_PIP_BASE] : [])
  ];
  return !screensWithoutBackButton.includes(screen);
};

/**
 * Get current intents by comparing previous and current intents
 * @param {Array} prevIntents - Previous intents array
 * @param {Array} currentIntents - Current intents array
 * @returns {Array} - Array of current intents
 * */
export const getCurrentIntents = (prevIntents = [], currentIntents = []) => {
  const { NEW, CHANGED, NO_CHANGE, REMOVED } = CHANGE_TYPE;
  if (!prevIntents || prevIntents.length === 0) {
    return currentIntents.map((group) => ({
      groupName: group.groupName,
      change: { type: NEW, previous: null },
      materials: group.materials.map((material) => ({
        intent: material.intent,
        quantity: material.quantity,
        change: { type: NEW, previous: null }
      }))
    }));
  }

  // Process each group in current intents
  return currentIntents.map((currentGroup) => {
    // Try to find matching group in previous intents
    const prevGroup = prevIntents.find((pIntent) => pIntent.id === currentGroup.id);

    // Determine group change type
    let groupChange = { type: NO_CHANGE, previous: null }; // Default: no change

    if (!prevGroup) {
      // New group
      groupChange = { type: NEW, previous: null };
    } else if (prevGroup.groupName !== currentGroup.groupName) {
      // Changed group name
      groupChange = { type: CHANGED, previous: prevGroup.groupName };
    }

    // Process materials in the group
    const processedMaterials = currentGroup.materials.map((currentMaterial) => {
      // Only search for matching material in previous group if the group exists
      const prevMaterial = prevGroup ? prevGroup.materials.find(
        (pMaterial) => pMaterial.id === currentMaterial.id
      ) : null;

      // Determine material change type
      let materialChange = { type: NO_CHANGE, previous: null }; // Default: no change

      if (!prevMaterial) {
        // New material
        materialChange = { type: NEW, previous: null };
      } else if (
        prevMaterial.intent !== currentMaterial.intent
        || prevMaterial.quantity !== currentMaterial.quantity
      ) {
        // Changed material (different intent text or quantity)
        materialChange = {
          type: CHANGED,
          previous: {
            intent: prevMaterial.intent,
            quantity: prevMaterial.quantity
          }
        };
      }

      return {
        intent: currentMaterial.intent,
        quantity: currentMaterial.quantity,
        change: materialChange
      };
    });

    // Check for removed materials (in prevGroup but not in currentGroup)
    const removedMaterials = prevGroup ? prevGroup.materials
      .filter((prevMat) => !currentGroup.materials.some((currMat) => currMat.id === prevMat.id))
      .map((removedMat) => ({
        intent: removedMat.intent,
        quantity: removedMat.quantity,
        change: { type: REMOVED, previous: null }
      })) : [];

    return {
      groupName: currentGroup.groupName,
      change: groupChange,
      materials: [...processedMaterials, ...removedMaterials]
    };
  });
};
